import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import {
  OPTIONS_AGREGATEUR_MANDAT, confirmationAttendue, etatGabarit, libelleAgregateur, saisieConforme,
} from '@/shared/utils/agregateurMandat';

const LIBELLES_ETAT = {
  generique: 'Modèle générique : il sert à tout mandat sans agrégateur dédié.',
  brouillon: 'Brouillon : ce modèle ne peut servir à aucun mandat tant qu’un autre administrateur ne l’a pas validé.',
  valide: 'Validé : ce modèle peut être activé pour les mandats de cet agrégateur.',
};

/**
 * À QUI S'ADRESSE CE MODÈLE, ET QUI A DIT QU'IL ÉTAIT BON (C8a, 15/10/2026).
 *
 * ⚠️ CHANGER DE DESTINATAIRE PASSE PAR UN DIALOGUE, JAMAIS PAR LE SEUL SELECT. La première
 * version appelait le serveur dès le choix : sur le modèle générique actif, un clic le
 * désactivait et tous les nouveaux mandats repartaient avec le document du code. Le
 * serveur refuse désormais ce cas (409) ; l'écran, lui, fait ressaisir le destinataire.
 *
 * Le brouillon suit la pièce : seule une validation par un AUTRE administrateur le lève
 * (nom de l'agrégateur ressaisi). C'est le garde-fou du texte juridique Symphonics, non
 * validé à ce jour : la mécanique est prête, la pièce se branchera ici, sans code.
 */
export default function RattachementAgregateur({ gabarit, onChange }) {
  const [cible, setCible] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const etat = etatGabarit(gabarit);
  const destinataireBrouillon = gabarit.brouillon_pour || gabarit.agregateur || '';

  const appeler = async (requete, succes) => {
    setEnvoi(true);
    try {
      await requete();
      toast.success(succes);
      setCible(null); setSaisie(''); setConfirmation('');
      await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opération impossible');
    }
    setEnvoi(false);
  };

  const rattacher = () => appeler(
    () => api.put(`/admin/mandat-templates/${gabarit.id}/agregateur`,
                  { agregateur: cible || null, confirmation: saisie }),
    cible ? `Modèle destiné à ${libelleAgregateur(cible)}, en brouillon` : 'Modèle rendu générique');

  const valider = () => appeler(
    () => api.put(`/admin/mandat-templates/${gabarit.id}/valider`, { confirmation }),
    'Modèle validé : il peut maintenant être activé');

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 flex flex-wrap items-end gap-3"
         data-testid="gabarit-rattachement">
      <div className="space-y-1">
        <label htmlFor="gabarit-agregateur" className="text-[11px] font-medium text-neutral-700">
          Destiné à
        </label>
        <select id="gabarit-agregateur" data-testid="gabarit-agregateur" disabled={envoi}
                value={gabarit.agregateur || ''}
                onChange={(e) => { setCible(e.target.value); setSaisie(''); }}
                className="h-8 rounded-md border border-neutral-200 px-2 text-[12px] bg-white">
          {OPTIONS_AGREGATEUR_MANDAT.map((o) => (
            <option key={o.valeur || 'generique'} value={o.valeur}>{o.libelle}</option>
          ))}
        </select>
      </div>
      <p className={`text-[11px] flex-1 min-w-[200px] ${etat === 'brouillon' ? 'text-amber-700' : 'text-neutral-500'}`}
         data-testid="gabarit-etat">
        {LIBELLES_ETAT[etat]}
        {etat === 'valide' && gabarit.valide_par && ` (par ${gabarit.valide_par})`}
      </p>
      {etat === 'brouillon' && (
        <div className="flex items-end gap-2">
          <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                 placeholder={`ressaisir « ${destinataireBrouillon} »`} className="h-8 w-48 text-[12px]"
                 aria-label="Nom de l'agrégateur, pour confirmer la validation"
                 data-testid="gabarit-confirmation" />
          <Button size="sm" variant="outline" onClick={valider} data-testid="gabarit-valider"
                  disabled={envoi || !saisieConforme(confirmation, destinataireBrouillon)}>
            Valider le modèle
          </Button>
        </div>
      )}

      <Dialog open={cible !== null} onOpenChange={(ouvert) => !ouvert && setCible(null)}>
        <DialogContent className="max-w-md" data-testid="gabarit-rattachement-dialogue">
          <DialogHeader>
            <DialogTitle className="text-base">Changer le destinataire de ce modèle</DialogTitle>
            <DialogDescription className="text-[13px] leading-relaxed">
              {cible
                ? `Le modèle sera destiné à ${libelleAgregateur(cible)}, désactivé et repassé en brouillon : il ne servira à aucun mandat avant d'être validé.`
                : 'Le modèle deviendra générique et sera désactivé. S\'il est en brouillon, il le restera jusqu\'à sa validation.'}
            </DialogDescription>
          </DialogHeader>
          <Input value={saisie} onChange={(e) => setSaisie(e.target.value)} autoFocus
                 placeholder={`ressaisir « ${confirmationAttendue(cible)} »`}
                 aria-label="Destinataire, pour confirmer" data-testid="gabarit-rattachement-saisie" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCible(null)}>Annuler</Button>
            <Button onClick={rattacher} data-testid="gabarit-rattachement-confirmer"
                    disabled={envoi || !saisieConforme(saisie, cible)}>
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
